<template>
  <v-dialog
    v-model="enableDialog"
    width="600"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
  >
    <v-card>
      <v-toolbar
        color="primary"
        dark
        dense
        flat
      >
        <v-toolbar-title class="white--text">
          現地情報
        </v-toolbar-title>
      </v-toolbar>
      
      <!-- ローディング表示 -->
      <v-overlay v-if="loading" absolute>
        <v-progress-circular indeterminate size="32"></v-progress-circular>
      </v-overlay>

      <v-card-text class="pa-4">
        <!-- 現地メモ -->
        <div>
          <h4 class="mb-2">現地メモ:</h4>
          <div v-if="orderLocalData.local_notes"
            class="mb-3 pa-2 grey lighten-4 rounded"
            style="white-space: pre-line; max-height: 200px; overflow-y: auto;"
          >
            {{ orderLocalData.local_notes }}
          </div>
        </div>
        
        <!-- 現地画像 -->
        <div>
          <h4 class="mb-2">
            <v-icon class="mr-1" color="primary">mdi-camera</v-icon>
            画像: {{ orderLocalData.photos.length }}枚
          </h4>
          <div  v-if="orderLocalData.photos && orderLocalData.photos.length > 0" class="d-flex flex-wrap">
            <div
              v-for="(photo, index) in orderLocalData.photos"
              :key="index"
              class="ma-1"
            >
              <v-card
                flat
                outlined
                style="width: 120px; height: 120px; position: relative; cursor: pointer;"
                @click="openImageViewer(photo, index)"
              >
                <v-img
                  :src="photo.thumb_url"
                  :alt="`画像 ${index + 1}`"
                  height="120"
                  contain
                >
                  <template v-slot:placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular indeterminate size="32"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
              </v-card>
            </div>
          </div>
        </div>
        
        <!-- データが空の場合 -->
        <div v-if="!loading && !orderLocalData.local_notes && (!orderLocalData.photos || orderLocalData.photos.length === 0)">
          <div class="text-center grey--text">
            現地メモ・写真情報がありません
          </div>
        </div>
      </v-card-text>
      
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn @click="closeDialog">
          閉じる
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- 画像ビューアー -->
    <v-dialog v-model="imageViewerDialog" max-width="90%" max-height="90%">
      <v-card v-if="selectedPhoto">
        <v-toolbar color="black" dark dense>
          <v-toolbar-title>画像 {{ selectedPhotoIndex + 1 }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="imageViewerDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-0">
          <v-img
            :src="selectedPhoto.photo_url"
            :alt="`画像 ${selectedPhotoIndex + 1}`"
            contain
            max-height="70vh"
          >
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate size="64"></v-progress-circular>
              </v-row>
            </template>
          </v-img>
        </v-card-text>
        <v-card-actions v-if="orderLocalData.photos && orderLocalData.photos.length > 1">
          <v-btn 
            icon 
            :disabled="selectedPhotoIndex === 0"
            @click="previousImage"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-spacer />
          <span class="caption">
            {{ selectedPhotoIndex + 1 }} / {{ orderLocalData.photos.length }}
          </span>
          <v-spacer />
          <v-btn 
            icon 
            :disabled="selectedPhotoIndex === orderLocalData.photos.length - 1"
            @click="nextImage"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: "QuickNotePreviewDialog",
  props: {
    /**
     * ダイアログ表示制御
     * @type {Boolean}
     * @default false
     */
    enableDialog: {
      type: Boolean,
      default: false
    },
    /**
     * 受注ID
     * @type {Number}
     * @default null
     */
    orderId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      orderLocalData: {
        local_notes: '',
        photos: []
      },
      imageViewerDialog: false,
      selectedPhoto: null,
      selectedPhotoIndex: 0
    }
  },
  watch: {
    enableDialog (newValue) {
      // データをリセット
      this.orderLocalData = {
        local_notes: '',
        photos: []
      };
      this.imageViewerDialog = false;
      this.selectedPhoto = null;
      this.selectedPhotoIndex = 0;
      
      if (newValue && this.orderId) {
        this.setVariable();
      }
    }
  },
  methods: {
    ...mapActions('DriverScheduleOrderLocal', {
      fetchOrderLocal: 'show'
    }),
    
    /**
     * 初期値の設定
     */
    setVariable () {
      if (!this.orderId) return;
      
      this.loading = true;
      this.orderLocalData = {
        local_notes: '',
        photos: []
      };
      
      this.fetchOrderLocal({ order_id: this.orderId }).then((content) => {
        this.orderLocalData = {
          local_notes: content.local_notes || '',
          photos: content.photos || []
        };
      }).catch((error) => {
        console.error('現地メモ・写真データの取得に失敗しました:', error);
        this.$emit('error', '現地メモ・写真データの取得に失敗しました');
      }).finally(() => {
        this.loading = false;
      });
    },
    
    /**
     * 画像ビューアーを開く
     * @param {Object} photo 写真オブジェクト
     * @param {Number} index インデックス
     */
    openImageViewer(photo, index) {
      this.selectedPhoto = photo;
      this.selectedPhotoIndex = index;
      this.imageViewerDialog = true;
    },
    
    /**
     * 前の画像を表示
     */
    previousImage() {
      if (this.selectedPhotoIndex > 0) {
        this.selectedPhotoIndex--;
        this.selectedPhoto = this.orderLocalData.photos[this.selectedPhotoIndex];
      }
    },
    
    /**
     * 次の画像を表示
     */
    nextImage() {
      if (this.selectedPhotoIndex < this.orderLocalData.photos.length - 1) {
        this.selectedPhotoIndex++;
        this.selectedPhoto = this.orderLocalData.photos[this.selectedPhotoIndex];
      }
    },
    
    /**
     * ダイアログを閉じる
     */
    closeDialog() {
      this.$emit('closeCallback');
    }
  }
}
</script>